import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useDropzone } from 'react-dropzone';
import { Upload } from 'lucide-react';

const AdvancedMoireEffectGenerator = () => {
  const [image, setImage] = useState(null);
  const [frequency, setFrequency] = useState(30);
  const [angle, setAngle] = useState(0);
  const [intensity, setIntensity] = useState(1);
  const canvasRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // New state for circle layers
  const [circleLayers, setCircleLayers] = useState([
    { centerX: 0.25, centerY: 0.25, isActive: true },
    { centerX: 0.75, centerY: 0.25, isActive: true },
    { centerX: 0.25, centerY: 0.75, isActive: true },
    { centerX: 0.75, centerY: 0.75, isActive: true },
  ]);

  // Add a new state variable for line thickness
  const [lineWidth, setLineWidth] = useState(1);

  // Add new state variable for line color
  const [lineColor, setLineColor] = useState('#000000');

  // Drag-and-drop handler
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    const reader = new FileReader();
    reader.onload = (e) => setImage(e.target.result);
    reader.readAsDataURL(file);
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: false,
  });

  // Memoize createPattern
  const createPattern = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const patternCanvas = document.createElement('canvas');
    const patternCtx = patternCanvas.getContext('2d');
    patternCanvas.width = canvas.width;
    patternCanvas.height = canvas.height;

    // Clear the canvas
    patternCtx.clearRect(0, 0, patternCanvas.width, patternCanvas.height);

    // Set the stroke style and line width
    patternCtx.strokeStyle = lineColor; // Use lineColor from state
    patternCtx.lineWidth = lineWidth;   // Use lineWidth from state

    // Apply rotation based on angle
    patternCtx.save();
    patternCtx.translate(patternCanvas.width / 2, patternCanvas.height / 2);
    patternCtx.rotate((angle * Math.PI) / 180);
    patternCtx.translate(-patternCanvas.width / 2, -patternCanvas.height / 2);

    // Draw pattern
    const maxRadius = Math.hypot(patternCanvas.width, patternCanvas.height);

    // Loop over each active circle layer
    circleLayers.forEach((layer) => {
      if (layer.isActive) {
        const centerX = patternCanvas.width * layer.centerX;
        const centerY = patternCanvas.height * layer.centerY;

        for (let r = 0; r <= maxRadius; r += frequency) {
          patternCtx.beginPath();
          patternCtx.arc(centerX, centerY, r, 0, 2 * Math.PI);
          patternCtx.stroke();
        }
      }
    });

    // Restore the canvas context
    patternCtx.restore();

    // Return the pattern
    const ctx = canvas.getContext('2d');
    return ctx.createPattern(patternCanvas, 'no-repeat');
  }, [circleLayers, frequency, angle, lineWidth, lineColor]); // Added 'lineColor' to dependencies

  // Memoize applyMoireEffect and include createPattern in dependencies
  const applyMoireEffect = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      tempCanvas.width = img.width;
      tempCanvas.height = img.height;

      // Draw the original image
      ctx.drawImage(img, 0, 0);

      // Create the pattern
      tempCtx.fillStyle = createPattern();
      tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

      // Draw the pattern over the image without blending
      ctx.globalAlpha = intensity;
      ctx.globalCompositeOperation = 'source-over'; // Changed from 'overlay' to 'source-over'
      ctx.drawImage(tempCanvas, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;

      // Update the preview URL
      setPreviewUrl(canvas.toDataURL());
    };
    img.src = image;
  }, [image, intensity, createPattern]); // Added 'createPattern' here

  // Update useEffect to include 'image' in dependencies
  useEffect(() => {
    if (image) {
      applyMoireEffect();
    }
  }, [image, applyMoireEffect]);

  return (
    <div className="min-h-screen bg-white p-8 flex flex-col items-center justify-center">
      <Helmet>
        <title>Advanced Moiré Effect Generator</title>
        <meta
          name="description"
          content="Generate stunning Moiré patterns with advanced controls and customization options."
        />
        <meta
          name="keywords"
          content="Moiré, Effect, Generator, Image Processing, React"
        />
      </Helmet>
      <h1 className="text-3xl font-bold mb-6 text-black">Advanced Moiré Effect Generator</h1>

      {/* Drag-and-Drop Area */}
      <div
        {...getRootProps()}
        className={`w-full max-w-lg p-8 rounded-lg border-4 border-dashed border-gray-400 transition-all duration-300 ease-in-out ${
          isDragActive ? 'border-gray-600' : 'border-gray-400'
        }`}
      >
        <input {...getInputProps()} />
        <div className="text-center">
          <Upload className="mx-auto h-12 w-12 text-gray-600 mb-4" />
          <p className="text-xl font-semibold text-gray-600">
            {isDragActive ? 'Drop image here' : 'Drag & drop image or click to select'}
          </p>
        </div>
      </div>

      {image && (
        <div className="w-full max-w-lg mt-6">
          {/* Controls */}
          <div className="mb-4">
            <label className="mr-2 text-black">Frequency:</label>
            <input
              type="range"
              min="2"
              max="15"
              value={frequency}
              onChange={(e) => setFrequency(Number(e.target.value))}
              className="w-full"
            />
          </div>
          <div className="mb-4">
            <label className="mr-2 text-black">Angle:</label>
            <input
              type="range"
              min="0"
              max="360"
              value={angle}
              onChange={(e) => setAngle(Number(e.target.value))}
              className="w-full"
            />
          </div>
          <div className="mb-4">
            <label className="mr-2 text-black">Intensity:</label>
            <input
              type="range"
              min="0.1"
              max="1"
              step="0.1"
              value={intensity}
              onChange={(e) => setIntensity(Number(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Circle Layer Controls */}
          <div className="mb-4">
            <p className="text-black font-semibold">Circle Layers:</p>
            {circleLayers.map((layer, index) => (
              <div key={index} className="flex items-center mb-2">
                <input
                  type="checkbox"
                  checked={layer.isActive}
                  onChange={() => {
                    const newLayers = [...circleLayers];
                    newLayers[index].isActive = !newLayers[index].isActive;
                    setCircleLayers(newLayers);
                  }}
                  className="mr-2"
                />
                <label className="text-black">Layer {index + 1}</label>
              </div>
            ))}
          </div>

          {/* Line Thickness Control */}
          <div className="mb-4">
            <label className="mr-2 text-black">Line Thickness:</label>
            <input
              type="range"
              min="0.1"
              max="10"
              step="0.1"
              value={lineWidth}
              onChange={(e) => setLineWidth(Number(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Line Color Control */}
          <div className="mb-4">
            <label className="mr-2 text-black">Line Color:</label>
            <input
              type="color"
              value={lineColor}
              onChange={(e) => setLineColor(e.target.value)}
              className="w-full h-10 p-1 border rounded"
            />
          </div>

          {previewUrl && (
            <img
              src={previewUrl}
              alt="Moire Effect Preview"
              className="border border-gray-300 mt-4"
            />
          )}
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default AdvancedMoireEffectGenerator;